import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip as MuiTooltip,
  Typography,
  Alert,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import TuneIcon from '@mui/icons-material/Tune';
import RefreshIcon from '@mui/icons-material/Refresh';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import TimelineIcon from '@mui/icons-material/Timeline';
import SpeedIcon from '@mui/icons-material/Speed';
import NotificationsPausedIcon from '@mui/icons-material/NotificationsPaused';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import LayersIcon from '@mui/icons-material/Layers';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  useVersionDistribution,
  useFleetUptime,
  useFleetHealth,
  useFleetTrends,
  useFleetEvents,
  useEnrollmentGrowth,
} from '@/hooks/use-analytics';
import { PanelFrame, usePanelVisibility } from '@/components/panel-frame';
import { DashboardGrid, useDashboardLayout, type GridPanel } from '@/components/dashboard-grid';
import { useFeatures } from '@/hooks/use-features';
import { colors, typography as t } from '@/theme/tokens';
import { metricColors, healthColor } from '@/theme/health';
import { AccessibleChart, VisuallyHiddenTable } from '@/components/chart-a11y';
import { StatCard } from '@/components/stat-card';
import { FormattedTime } from '@/components/ui/formatted-time';
import { CopyDeviceId } from '@/components/ui/copy-button';
import { InternalDeviceIcon } from '@/components/ui/internal-device-icon';
import { FeatureBadge } from '@/components/ui/demo-feature';
import { downloadBlob } from '@/lib/download';
import { notificationStore } from '@/stores/notification.store';
import type { FleetEventType } from '@/types';

// ─── Panel registry ──────────────────────────────────────────────────
// Grafana-style v1: per-panel show/hide persisted in localStorage +
// per-panel period selectors. Order is the registry order (no drag in v1 —
// matches the device Live Dashboard's capabilities).

type FleetPanelId =
  | 'health'
  | 'silent'
  | 'online-trend'
  | 'cpu-mem-trend'
  | 'worst-uptime'
  | 'versions'
  | 'growth'
  | 'events';

interface FleetPanelDef {
  id: FleetPanelId;
  title: string;
  icon: React.ReactNode;
  /** Default position/size on the draggable grid (12 cols, ~48px rows). */
  layout: { x: number; y: number; w: number; h: number };
}

// Default dashboard layout, 2-up, ordered by importance. Users can drag/resize
// freely; the layout is saved per-browser and a Reset restores these defaults.
const FLEET_PANELS: FleetPanelDef[] = [
  { id: 'silent', title: 'Needs Service', icon: <NotificationsPausedIcon sx={{ fontSize: 16 }} />, layout: { x: 0, y: 0, w: 6, h: 6 } },
  { id: 'online-trend', title: 'Machines In Use', icon: <TimelineIcon sx={{ fontSize: 16 }} />, layout: { x: 6, y: 0, w: 6, h: 6 } },
  { id: 'health', title: 'Heartbeat Health', icon: <MonitorHeartIcon sx={{ fontSize: 16 }} />, layout: { x: 0, y: 6, w: 6, h: 6 } },
  { id: 'cpu-mem-trend', title: 'Fleet CPU / Memory', icon: <SpeedIcon sx={{ fontSize: 16 }} />, layout: { x: 6, y: 6, w: 6, h: 6 } },
  { id: 'worst-uptime', title: 'Lowest Availability', icon: <TrendingDownIcon sx={{ fontSize: 16 }} />, layout: { x: 6, y: 12, w: 6, h: 6 } },
  { id: 'events', title: 'Device Events', icon: <RestartAltIcon sx={{ fontSize: 16 }} />, layout: { x: 0, y: 18, w: 6, h: 6 } },
  { id: 'versions', title: 'Version Distribution', icon: <LayersIcon sx={{ fontSize: 16 }} />, layout: { x: 6, y: 18, w: 6, h: 6 } },
  { id: 'growth', title: 'Enrollment Growth', icon: <TrendingUpIcon sx={{ fontSize: 16 }} />, layout: { x: 0, y: 24, w: 6, h: 6 } },
];

const DEFAULT_VISIBILITY: Record<FleetPanelId, boolean> = {
  health: true,
  silent: true,
  'online-trend': true,
  'cpu-mem-trend': true,
  'worst-uptime': true,
  versions: true,
  growth: true,
  events: true,
};

// ─── Freshness reporting ─────────────────────────────────────────────
// Panels are lazily mounted/unmounted, so the page can't read their query
// `dataUpdatedAt` directly. Each panel reports its latest dataUpdatedAt up
// through this context; the header shows the most recent across all mounted
// panels. Unmounting a panel simply stops it reporting — the remaining
// panels still drive the freshness caption.

const FreshnessContext = createContext<(ts: number) => void>(() => {});

function useReportFreshness(dataUpdatedAt: number | undefined) {
  const report = useContext(FreshnessContext);
  useEffect(() => {
    if (dataUpdatedAt && dataUpdatedAt > 0) report(dataUpdatedAt);
  }, [dataUpdatedAt, report]);
}

// ─── Shared helpers ──────────────────────────────────────────────────

function fmtBucketTime(ts: string, longForm: boolean): string {
  const d = new Date(ts);
  if (longForm) {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function humanizeSeconds(sec: number): string {
  if (sec <= 0) return 'unknown';
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 24 * 3600) return `${Math.round(sec / 3600)}h`;
  return `${Math.round(sec / (24 * 3600))}d`;
}

const longFormPeriods = new Set(['7d', '30d']);

// "Needs Service" threshold: a fitness machine dark this long is an anomaly
// (unplugged / broken / returned), not normal idle. Tenant-confirmed at 7 days.
const INACTIVE_DAYS = 7;
// Cap the inline list so the panel stays scannable (no nested scrollbar);
// the rest live in the device list.
const NEEDS_SERVICE_VISIBLE = 8;

// ─── Global time range ───────────────────────────────────────────────
// One Time Range selector in the header drives every time-windowed panel.
// Each panel declares the ranges it supports and clamps the global choice to
// the nearest one (Fleet Uptime has no sub-day windows; Heartbeat Health has
// no 30d/90d), so a single control stays consistent across the page. Panels
// without a time window (Versions, Enrollment Growth) keep their own controls.

export type FleetRange = '1h' | '6h' | '24h' | '7d' | '30d';
const FLEET_RANGES: FleetRange[] = ['1h', '6h', '24h', '7d', '30d'];
const RANGE_LABELS: Record<FleetRange, string> = {
  '1h': 'Last 1 hour',
  '6h': 'Last 6 hours',
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
};
const RANGE_RANK: Record<FleetRange, number> = { '1h': 0, '6h': 1, '24h': 2, '7d': 3, '30d': 4 };

const FleetRangeContext = createContext<FleetRange>('24h');

// Clamp the global range to the nearest range a panel actually supports.
function useClampedRange(allowed: FleetRange[]): FleetRange {
  const global = useContext(FleetRangeContext);
  if (allowed.includes(global)) return global;
  const rank = RANGE_RANK[global];
  return allowed.reduce(
    (best, a) => (Math.abs(RANGE_RANK[a] - rank) < Math.abs(RANGE_RANK[best] - rank) ? a : best),
    allowed[0],
  );
}

function PanelLoading() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1, py: 4 }}>
      <CircularProgress size={24} />
    </Box>
  );
}

function PanelError({ what, onRetry }: { what: string; onRetry?: () => void }) {
  return (
    <Alert
      severity="error"
      variant="outlined"
      sx={{ m: 1 }}
      action={
        onRetry ? (
          <Button color="inherit" size="small" onClick={() => onRetry()}>
            Retry
          </Button>
        ) : undefined
      }
    >
      Failed to load {what}.
    </Alert>
  );
}

function PeriodToggle<T extends string>({ value, onChange, options }: {
  value: T;
  onChange: (v: T) => void;
  options: T[];
}) {
  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={(_, v) => v && onChange(v as T)}
      size="small"
      sx={{ '& .MuiToggleButton-root': { py: 0, px: 1, textTransform: 'none', fontSize: 11 } }}
    >
      {options.map((p) => (
        <ToggleButton key={p} value={p}>{p}</ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

// ─── Panels ──────────────────────────────────────────────────────────

function HealthPanel() {
  const period = useClampedRange(['1h', '6h', '24h', '7d']);
  const { data, isLoading, isError, refetch, dataUpdatedAt } = useFleetHealth(period);
  useReportFreshness(dataUpdatedAt);

  if (isError) return <PanelError what="fleet health" onRetry={refetch} />;
  if (isLoading || !data) return <PanelLoading />;

  const worst = data.worst_devices ?? [];
  const avg = data.avg_coverage_percent;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h3" fontWeight={700} color="text.primary" sx={{ lineHeight: 1 }}>
            {avg.toFixed(1)}%
          </Typography>
          <Chip size="small" sx={{ bgcolor: '#F4F4F4' }} label={`${data.reporting_devices}/${data.total_devices} reporting`} />
        </Box>
        <MuiTooltip title="Share of expected heartbeats received, averaged across enrolled devices (clamped to enrollment). For fitness machines this mostly tracks USAGE, not health — a low value usually just means the machines weren't in use, not that anything is broken.">
          <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'underline dotted', textUnderlineOffset: 3, cursor: 'help' }}>
            Avg heartbeat coverage — reporting, not a fault score
          </Typography>
        </MuiTooltip>
      </Box>
      {worst.length > 0 ? (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Least-reporting devices (lowest first) — click a name to open. Low usually just means "not in use".
          </Typography>
          {/* Replaced the recharts onClick bar (mouse-only) with an accessible
              table mirroring the Silent Devices table — every row is a real
              keyboard-navigable RouterLink. */}
          <Box sx={{ overflow: 'auto', maxHeight: 280 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Device</TableCell>
                  <TableCell align="right">Coverage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {worst.map((d) => {
                  const label = d.name || d.machine_id;
                  return (
                    <TableRow key={d.id || d.machine_id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Link component={RouterLink} to={`/devices/${d.id}`} underline="hover">
                            {label}
                          </Link>
                          <InternalDeviceIcon internal={d.is_internal} size={14} />
                          <CopyDeviceId machineId={d.machine_id} size={14} sx={{ p: 0.25 }} />
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <MuiTooltip title={`${d.beat_count}/${d.expected_beats} beats received`}>
                          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {d.coverage_percent.toFixed(1)}%
                          </Box>
                        </MuiTooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">No devices reported in this window.</Typography>
      )}
    </Box>
  );
}

function SilentPanel() {
  // Decoupled from the global range: "needs service" is a multi-day anomaly,
  // not a windowed view. Always evaluate over a 7-day window.
  const { data, isLoading, isError, refetch, dataUpdatedAt } = useFleetHealth('7d');
  useReportFreshness(dataUpdatedAt);

  if (isError) return <PanelError what="the needs-service list" onRetry={refetch} />;
  if (isLoading || !data) return <PanelLoading />;

  // Only flag machines dark for INACTIVE_DAYS+ — a machine used recently is
  // never a problem, regardless of the selected range.
  const needsService = (data.silent_devices ?? []).filter(
    (s) => s.silent_for_seconds >= INACTIVE_DAYS * 86_400,
  );
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
      {needsService.length === 0 ? (
        <Alert severity="success" variant="outlined">
          No machines inactive for {INACTIVE_DAYS}+ days — every enrolled machine has checked in recently.
        </Alert>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary">
            No heartbeat for {INACTIVE_DAYS}+ days — likely unplugged, broken, or returned (not just idle overnight). Longest first.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Device</TableCell>
                <TableCell>Inactive for</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {needsService.slice(0, NEEDS_SERVICE_VISIBLE).map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Link component={RouterLink} to={`/devices/${s.id}`} underline="hover">
                        {s.name || s.machine_id}
                      </Link>
                      <InternalDeviceIcon internal={s.is_internal} size={14} />
                      <CopyDeviceId machineId={s.machine_id} size={14} sx={{ p: 0.25 }} />
                    </Box>
                  </TableCell>
                  <TableCell>{humanizeSeconds(s.silent_for_seconds)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {needsService.length > NEEDS_SERVICE_VISIBLE && (
            <Typography variant="caption" color="text.secondary">
              +{needsService.length - NEEDS_SERVICE_VISIBLE} more{needsService.length >= 100 ? ' (100+ total)' : ''} inactive ≥ {INACTIVE_DAYS} days — open the device list to see all.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

function OnlineTrendPanel() {
  const period = useClampedRange(['1h', '6h', '24h', '7d', '30d']);
  const { data, isLoading, isError, refetch, dataUpdatedAt } = useFleetTrends(period);
  useReportFreshness(dataUpdatedAt);

  if (isError) return <PanelError what="fleet trends" onRetry={refetch} />;
  if (isLoading || !data) return <PanelLoading />;

  const longForm = longFormPeriods.has(period);
  const buckets = data.buckets ?? [];
  const chart = buckets.map((b) => ({
    time: fmtBucketTime(b.timestamp, longForm),
    online: b.online_devices,
  }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Machines in use (sent at least one heartbeat) per time bucket — low overnight is normal.
      </Typography>
      {chart.length === 0 ? (
        <Alert severity="info" variant="outlined">
          No reporting data in the last {period}.
        </Alert>
      ) : (
        <>
          <AccessibleChart
            label="Devices reporting over time"
            description={`Distinct devices with a heartbeat per bucket, last ${period}.`}
            sx={{ height: 220 }}
          >
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip formatter={(v: number) => [v, 'Devices reporting']} />
                <Line type="monotone" dataKey="online" name="Devices reporting" stroke={metricColors.cpu} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </AccessibleChart>
          <VisuallyHiddenTable
            caption={`Devices reporting per bucket, last ${period}`}
            columns={['Bucket', 'Devices reporting']}
            rows={chart.map((c) => [c.time, c.online])}
          />
        </>
      )}
    </Box>
  );
}

function CpuMemTrendPanel() {
  const period = useClampedRange(['1h', '6h', '24h', '7d', '30d']);
  const [metric, setMetric] = useState<'cpu' | 'memory'>('cpu');
  const { data, isLoading, isError, refetch, dataUpdatedAt } = useFleetTrends(period);
  useReportFreshness(dataUpdatedAt);

  if (isError) return <PanelError what="fleet trends" onRetry={refetch} />;
  if (isLoading || !data) return <PanelLoading />;

  const longForm = longFormPeriods.has(period);
  const buckets = data.buckets ?? [];
  const chart = buckets.map((b) => ({
    time: fmtBucketTime(b.timestamp, longForm),
    avg: metric === 'cpu' ? Math.round(b.avg_cpu_percent * 10) / 10 : Math.round(b.avg_memory_kb / 1024),
    p95: metric === 'cpu' ? Math.round(b.p95_cpu_percent * 10) / 10 : Math.round(b.p95_memory_kb / 1024),
  }));
  const unit = metric === 'cpu' ? '%' : ' MB';
  const metricLabel = metric === 'cpu' ? 'CPU' : 'Memory';
  const color = metricColors[metric];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Fleet-wide {metricLabel.toLowerCase()} per bucket — avg solid, p95 dashed (p95 catches the hottest tail).
      </Typography>
      {chart.length === 0 ? (
        <Alert severity="info" variant="outlined">
          No metric samples in the last {period}.
        </Alert>
      ) : (
        <>
          <AccessibleChart
            label={`Fleet ${metricLabel} trend`}
            description={`Average and p95 ${metricLabel.toLowerCase()} per bucket, last ${period}.`}
            sx={{ height: 220 }}
          >
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} domain={metric === 'cpu' ? [0, 100] : undefined} tickFormatter={(v) => `${v}${unit}`} />
                <Tooltip formatter={(value: number, name: string) => [`${value}${unit}`, name]} />
                <Legend />
                <Line type="monotone" dataKey="avg" name={`Avg ${metricLabel}`} stroke={color} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="p95" name={`P95 ${metricLabel}`} stroke={`${color}66`} strokeWidth={1} strokeDasharray="4 2" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </AccessibleChart>
          <VisuallyHiddenTable
            caption={`Fleet ${metricLabel} per bucket, last ${period}`}
            columns={['Bucket', `Avg ${metricLabel}${unit}`, `P95 ${metricLabel}${unit}`]}
            rows={chart.map((c) => [c.time, c.avg, c.p95])}
          />
        </>
      )}
      <Box sx={{ mt: 'auto', display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <PeriodToggle value={metric} onChange={setMetric} options={['cpu', 'memory']} />
      </Box>
    </Box>
  );
}

// Surfaces the worst-availability devices the fleet-uptime endpoint already
// returns (roster-joined + navigable). Previously the KPI band read only the
// fleet average and discarded this list, so "which machines spend the most time
// powered off?" had no answer in the UI.
function WorstUptimePanel() {
  const period = useClampedRange(['7d', '30d']);
  const { data, isLoading, isError, refetch, dataUpdatedAt } = useFleetUptime(period);
  useReportFreshness(dataUpdatedAt);

  if (isError) return <PanelError what="fleet uptime" onRetry={refetch} />;
  if (isLoading || !data) return <PanelLoading />;

  const devices = (data.devices ?? []).slice(0, 10);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h3" fontWeight={700} color="text.primary" sx={{ lineHeight: 1 }}>
          {data.avg_uptime_percent.toFixed(0)}%
        </Typography>
        <Typography variant="body2" color="text.secondary">
          avg powered on · {data.total_devices} device{data.total_devices !== 1 ? 's' : ''}
        </Typography>
      </Box>
      <MuiTooltip title="Share of the window each machine was powered on (connection runs, not workouts). Lowest first — these spend the most time off / unplugged.">
        <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'underline dotted', textUnderlineOffset: 3, cursor: 'help' }}>
          Lowest availability — click a name to open
        </Typography>
      </MuiTooltip>
      {devices.length === 0 ? (
        <Alert severity="info" variant="outlined">No uptime data in this window.</Alert>
      ) : (
        <Box sx={{ overflow: 'auto', maxHeight: 280 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Device</TableCell>
                <TableCell align="right">Powered on</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.map((d) => {
                const label = d.name || d.device_id;
                const cell = (
                  <Box component="span" sx={{ fontWeight: 600, color: healthColor(d.uptime_percent) }}>
                    {d.uptime_percent.toFixed(0)}%
                  </Box>
                );
                return (
                  <TableRow key={d.id || d.device_id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {d.id ? (
                          <Link component={RouterLink} to={`/devices/${d.id}`} underline="hover">{label}</Link>
                        ) : (
                          <MuiTooltip title={`${label} — device not in roster`}>
                            <Box component="span" sx={{ color: 'text.disabled' }}>{label}</Box>
                          </MuiTooltip>
                        )}
                        <InternalDeviceIcon internal={d.is_internal} size={13} />
                        <CopyDeviceId machineId={d.device_id} size={13} sx={{ p: 0.25 }} />
                      </Box>
                    </TableCell>
                    <TableCell align="right">{cell}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

const versionTypeLabels: Record<string, string> = {
  agent: 'Agent Version',
  rom: 'ROM Version',
};

function VersionsPanel() {
  // Android version is not reported by the Aviron device protocol, so the
  // column is always empty (every device → 'unknown'). Tab omitted rather
  // than showing a permanently-empty chart.
  const [versionType, setVersionType] = useState<'agent' | 'rom'>('agent');
  const { data, isLoading, isError, refetch, dataUpdatedAt } = useVersionDistribution(versionType);
  useReportFreshness(dataUpdatedAt);

  if (isError) return <PanelError what="version distribution" onRetry={refetch} />;
  if (isLoading || !data) return <PanelLoading />;

  const distribution = data.distribution ?? [];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
      {distribution.length > 0 ? (
        <>
          <AccessibleChart
            label={`${versionTypeLabels[versionType]} distribution`}
            description={`Device count per ${versionTypeLabels[versionType].toLowerCase()}.`}
            sx={{ height: 240 }}
          >
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={distribution} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="version" fontSize={12} />
                <YAxis allowDecimals={false} />
                <Tooltip
                  formatter={(value: number) => [value, 'Devices']}
                  labelFormatter={(label: string) => `${versionTypeLabels[versionType]}: ${label}`}
                />
                <Bar dataKey="device_count" fill={metricColors.cpu} name="Devices" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </AccessibleChart>
          <VisuallyHiddenTable
            caption={`${versionTypeLabels[versionType]} distribution`}
            columns={[versionTypeLabels[versionType], 'Devices']}
            rows={distribution.map((d) => [d.version, d.device_count])}
          />
        </>
      ) : (
        <Alert severity="info" variant="outlined">
          No version data available. Devices show up here once enrolled.
        </Alert>
      )}
      <Box sx={{ mt: 'auto', display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <PeriodToggle value={versionType} onChange={setVersionType} options={['agent', 'rom']} />
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() => {
            // Route through the authenticated axios instance — a bare
            // window.open skips the Bearer-token interceptor (→ 401) and
            // resolves against the frontend origin instead of the API host.
            downloadBlob(
              `/analytics/version-distribution?type=${versionType}&format=csv`,
              `${versionType}-version-distribution.csv`,
            ).catch(() =>
              notificationStore.getState().show('CSV export failed. Please try again.', 'error'),
            );
          }}
        >
          CSV
        </Button>
      </Box>
    </Box>
  );
}

function GrowthPanel() {
  const [weeks, setWeeks] = useState<'12' | '26' | '52'>('12');
  const { data, isLoading, isError, refetch, dataUpdatedAt } = useEnrollmentGrowth(Number(weeks));
  useReportFreshness(dataUpdatedAt);

  if (isError) return <PanelError what="enrollment growth" onRetry={refetch} />;
  if (isLoading || !data) return <PanelLoading />;

  const chart = (data.weeks ?? []).map((w) => ({
    week: new Date(w.week_start).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    devices: w.new_devices,
  }));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {data.total} device{data.total !== 1 ? 's' : ''} enrolled in the last {weeks} weeks.
      </Typography>
      {chart.length > 0 ? (
        <>
          <AccessibleChart
            label="Enrollment growth"
            description={`New devices enrolled per week, last ${weeks} weeks.`}
            sx={{ height: 220 }}
          >
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: number) => [v, 'New devices']} />
                <Bar dataKey="devices" fill={colors.success.main} name="New devices" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </AccessibleChart>
          <VisuallyHiddenTable
            caption={`New devices per week, last ${weeks} weeks`}
            columns={['Week', 'New devices']}
            rows={chart.map((c) => [c.week, c.devices])}
          />
        </>
      ) : (
        <Alert severity="info" variant="outlined">No enrollments in this window.</Alert>
      )}
      <Box sx={{ mt: 'auto' }}>
        <PeriodToggle value={weeks} onChange={setWeeks} options={['12', '26', '52']} />
      </Box>
    </Box>
  );
}

const eventTypeLabels: Record<FleetEventType, string> = {
  boot: 'Boots',
  presence_online: 'Online events',
  presence_offline: 'Offline events',
};

function EventsPanel() {
  const [eventType, setEventType] = useState<FleetEventType>('boot');
  const period = useClampedRange(['6h', '24h', '7d', '30d']);
  const { data, isLoading, isError, refetch, dataUpdatedAt } = useFleetEvents(eventType, period);
  useReportFreshness(dataUpdatedAt);

  if (isError) return <PanelError what="device events" onRetry={refetch} />;
  if (isLoading || !data) return <PanelLoading />;

  const longForm = longFormPeriods.has(period);
  const buckets = data.buckets ?? [];
  const chart = buckets.map((b) => ({
    time: fmtBucketTime(b.timestamp, longForm),
    count: b.count,
  }));
  const top = data.top_devices ?? [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {data.total_events.toLocaleString()} {eventTypeLabels[eventType].toLowerCase()} in window.
        Frequent rebooters / flappers stand out in the top list.
      </Typography>
      {chart.length === 0 ? (
        <Alert severity="info" variant="outlined">
          {data.total_events === 0
            ? `No ${eventTypeLabels[eventType].toLowerCase()} in the last ${period}.`
            : `No bucketed event data for the last ${period}.`}
        </Alert>
      ) : (
        <>
          <AccessibleChart
            label={`${eventTypeLabels[eventType]} over time`}
            description={`${eventTypeLabels[eventType]} per bucket, last ${period}.`}
            sx={{ height: 180 }}
          >
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: number) => [v, eventTypeLabels[eventType]]} />
                <Bar dataKey="count" fill={colors.warning.main} name={eventTypeLabels[eventType]} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </AccessibleChart>
          <VisuallyHiddenTable
            caption={`${eventTypeLabels[eventType]} per bucket, last ${period}`}
            columns={['Bucket', eventTypeLabels[eventType]]}
            rows={chart.map((c) => [c.time, c.count])}
          />
        </>
      )}
      {top.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">Top devices:</Typography>
          {top.slice(0, 5).map((d) => {
            const label = d.name || d.device_id;
            const display = `${label.length > 12 ? `${label.slice(0, 10)}…` : label} ×${d.count}`;
            const chip = (
              <Chip
                size="small"
                variant="outlined"
                label={display}
                sx={{ fontFamily: 'monospace', fontSize: 11, color: d.id ? undefined : 'text.disabled' }}
              />
            );
            return (
              <Box key={d.device_id} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                {d.id ? (
                  <Link component={RouterLink} to={`/devices/${d.id}`} underline="none">
                    <MuiTooltip title={label}>{chip}</MuiTooltip>
                  </Link>
                ) : (
                  <MuiTooltip title={`${label} — device not in roster`}>
                    <Box component="span">{chip}</Box>
                  </MuiTooltip>
                )}
                <InternalDeviceIcon internal={d.is_internal} size={13} />
              </Box>
            );
          })}
        </Box>
      )}
      <Box sx={{ mt: 'auto', display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          value={eventType}
          exclusive
          onChange={(_, v) => v && setEventType(v as FleetEventType)}
          size="small"
          sx={{ '& .MuiToggleButton-root': { py: 0, px: 1, textTransform: 'none', fontSize: 11 } }}
        >
          <ToggleButton value="boot">boot</ToggleButton>
          <ToggleButton value="presence_offline">offline</ToggleButton>
          <ToggleButton value="presence_online">online</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
}

// Panels render their content lazily: a hidden panel is unmounted, so its
// React Query hook never runs (hidden panels cost nothing).
function panelBody(id: FleetPanelId) {
  switch (id) {
    case 'health': return <HealthPanel />;
    case 'silent': return <SilentPanel />;
    case 'online-trend': return <OnlineTrendPanel />;
    case 'cpu-mem-trend': return <CpuMemTrendPanel />;
    case 'worst-uptime': return <WorstUptimePanel />;
    case 'versions': return <VersionsPanel />;
    case 'growth': return <GrowthPanel />;
    case 'events': return <EventsPanel />;
  }
}

// ─── KPI summary band ────────────────────────────────────────────────
// TS-backed; mounted only when analytics is enabled so its hooks never run
// on deployments without a time-series backend.

function KpiBand() {
  const navigate = useNavigate();
  // The reporting/silent numbers are window-relative, so surface the effective
  // window in the label + tooltip rather than implying live ("now") state.
  const healthRange = useClampedRange(['1h', '6h', '24h', '7d']);
  const health = useFleetHealth(healthRange);
  const uptime = useFleetUptime(useClampedRange(['7d', '30d']));
  // "Needs Service" uses the SAME fixed 7-day anomaly definition as the panel
  // below it (not the selected range), so the KPI can't swing with an overnight
  // 1h window. Shares the fleetHealth('7d') query key with SilentPanel — no
  // extra request.
  const needsServiceHealth = useFleetHealth('7d');
  const report = useContext(FreshnessContext);
  useEffect(() => {
    if (health.dataUpdatedAt) report(health.dataUpdatedAt);
  }, [health.dataUpdatedAt, report]);
  useEffect(() => {
    if (uptime.dataUpdatedAt) report(uptime.dataUpdatedAt);
  }, [uptime.dataUpdatedAt, report]);

  const h = health.data;
  const total = h?.total_devices ?? 0;
  const reporting = h?.reporting_devices ?? 0;
  const reportingPct = total > 0 ? Math.round((reporting / total) * 100) : 0;
  const avgUptime = uptime.data?.avg_uptime_percent ?? 0;
  const needsService = (needsServiceHealth.data?.silent_devices ?? []).filter(
    (s) => s.silent_for_seconds >= INACTIVE_DAYS * 86_400,
  ).length;

  const healthLoading = health.isLoading;
  const uptimeLoading = uptime.isLoading;
  const rangeLabel = RANGE_LABELS[healthRange].replace(/^Last /, 'last ');

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', lg: 'nowrap' } }}>
      <StatCard
        label="Total Devices"
        value={total}
        isLoading={healthLoading}
        onClick={() => navigate('/devices')}
      />
      <StatCard
        label={`Reporting (${rangeLabel})`}
        value={reporting}
        percent={total > 0 ? reportingPct : undefined}
        isLoading={healthLoading}
        tooltip={`Devices with at least one heartbeat in the ${rangeLabel}. For fitness machines a low value usually just means they weren't in use — not that anything is broken.`}
      />
      <StatCard
        label="Needs Service"
        value={needsService}
        isLoading={needsServiceHealth.isLoading}
        tooltip={`Machines with no heartbeat for ${INACTIVE_DAYS}+ days — likely unplugged, broken, or returned. Fixed 7-day window, matching the Needs Service panel below.`}
        onClick={() => navigate('/devices?status=offline')}
      />
      <StatCard
        label="Avg Powered On"
        value={avgUptime}
        valueText={`${avgUptime.toFixed(0)}%`}
        isLoading={uptimeLoading}
        tooltip="Average share of the window each machine was powered on (connection runs, not workouts)."
      />
    </Box>
  );
}

// ─── Page ────────────────────────────────────────────────────────────

// Panels backed by the time-series store — hidden on deployments with no
// analytics backend (versions + growth are pure postgres and always work).
const TS_BACKED_PANELS = new Set<FleetPanelId>([
  'health', 'silent', 'online-trend', 'cpu-mem-trend', 'worst-uptime', 'events',
]);

export function AnalyticsPage() {
  const [visibility, togglePanel] = usePanelVisibility<FleetPanelId>('fleet-analytics-panels', DEFAULT_VISIBILITY);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [range, setRange] = useState<FleetRange>('24h');
  const features = useFeatures();
  const queryClient = useQueryClient();
  const analyticsEnabled = features.analytics ?? features.clickhouse;
  const availablePanels = FLEET_PANELS.filter((p) => analyticsEnabled || !TS_BACKED_PANELS.has(p.id));

  // Freshness: panels push their latest dataUpdatedAt; we keep the max.
  const [lastUpdatedAt, setLastUpdatedAt] = useState(0);
  const reportFreshness = useCallback((ts: number) => {
    setLastUpdatedAt((prev) => (ts > prev ? ts : prev));
  }, []);

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['analytics'] });
  }, [queryClient]);

  const lastUpdatedIso = useMemo(
    () => (lastUpdatedAt > 0 ? new Date(lastUpdatedAt).toISOString() : undefined),
    [lastUpdatedAt],
  );

  // Visible panels become draggable/resizable grid items; layout is persisted.
  const visiblePanels = availablePanels.filter((p) => visibility[p.id]);
  const gridPanels: GridPanel[] = visiblePanels.map((p) => ({
    id: p.id,
    ...p.layout,
    minW: 3,
    minH: 3,
    render: () => (
      <PanelFrame title={p.title} icon={p.icon} draggable fill>
        {panelBody(p.id)}
      </PanelFrame>
    ),
  }));
  const { layouts, onLayoutChange, reset: resetLayout } = useDashboardLayout('fleet-analytics-layout', gridPanels);

  return (
    <FreshnessContext.Provider value={reportFreshness}>
     <FleetRangeContext.Provider value={range}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: t.fontSize.xl, fontWeight: t.fontWeight.bold, color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1.25 }}>
              Fleet Analytics
              <FeatureBadge label="Beta" tone="beta" />
            </Typography>
            <Typography sx={{ fontSize: t.fontSize.base, color: 'text.secondary', mt: 0.5 }}>
              Fleet-wide availability, performance and inventory trends
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {lastUpdatedIso && (
              <Typography sx={{ fontSize: t.fontSize.sm, color: 'text.secondary' }}>
                Updated{' '}
                <FormattedTime
                  value={lastUpdatedIso}
                  variant="relative"
                  component="span"
                  sx={{ fontSize: t.fontSize.sm, color: 'text.secondary' }}
                />
              </Typography>
            )}
            {analyticsEnabled && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={range}
                  onChange={(e) => setRange(e.target.value as FleetRange)}
                  inputProps={{ 'aria-label': 'Time range' }}
                >
                  {FLEET_RANGES.map((r) => (
                    <MenuItem key={r} value={r}>{RANGE_LABELS[r]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <MuiTooltip title="Refresh analytics">
              <IconButton
                onClick={handleRefresh}
                size="small"
                aria-label="Refresh analytics data"
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </MuiTooltip>
            <MuiTooltip title="Show / hide panels">
              <Button
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                variant="outlined"
                size="small"
                startIcon={<TuneIcon />}
                aria-label="Show or hide analytics panels"
                sx={{ borderColor: 'divider', color: 'text.secondary', textTransform: 'none' }}
              >
                Panels
              </Button>
            </MuiTooltip>
            <MuiTooltip title="Reset panel layout to default">
              <Button
                onClick={resetLayout}
                variant="outlined"
                size="small"
                aria-label="Reset panel layout"
                sx={{ borderColor: 'divider', color: 'text.secondary', textTransform: 'none' }}
              >
                Reset layout
              </Button>
            </MuiTooltip>
          </Box>
          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={() => setMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {availablePanels.map((p) => (
              <MenuItem key={p.id} dense onClick={() => togglePanel(p.id)}>
                <Checkbox size="small" checked={visibility[p.id]} sx={{ p: 0.5, mr: 1 }} />
                {p.title}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* KPI summary band — TS-backed, only when analytics is enabled. */}
        {analyticsEnabled && <KpiBand />}

        {/* Draggable + resizable panel grid — drag a panel by its title bar,
            resize from the bottom-right corner; layout saved per-browser. */}
        <DashboardGrid panels={gridPanels} layouts={layouts} onLayoutChange={onLayoutChange} />
      </Box>
     </FleetRangeContext.Provider>
    </FreshnessContext.Provider>
  );
}

export default AnalyticsPage;
